import { useEffect, useMemo, useState } from "react";
import { pca } from "@/auth/AuthProvider";
import { CitationDetailPanel } from "@/components/citations/CitationDetailPanel";
import { Composer } from "@/components/composer/Composer";
import { JobsSection } from "@/components/history/JobsSection";
import { ArtifactPanel } from "@/components/thread/ArtifactPanel";
import { Thread } from "@/components/thread/Thread";
import { isActive } from "@/lib/jobStatusBadge";
import { SHORTCUT, shortcut } from "@/lib/shortcuts";
import { useAutoScroll } from "@/lib/useAutoScroll";
import { useJobsPolling } from "@/lib/useJobsPolling";
import { useJobsPollingDriver } from "@/lib/useJobsPollingDriver";
import { useMessageAnchorScroll } from "@/lib/useMessageAnchorScroll";
import { useMyTenants } from "@/lib/useMyTenants";
import { useOpenJobFromHistory } from "@/lib/useOpenJobFromHistory";
import { useSettings } from "@/lib/useSettings";
import { useShortcut } from "@/lib/useShortcut";
import { useUrlConversationSync } from "@/lib/useUrlConversationSync";
import { useChatStore } from "@/runtime/chatStore";
import { useStreamWatchdog } from "@/runtime/useStreamWatchdog";
import { BrandLockup, BrandSymbol } from "./BrandLogo";
import { type CommandAction, CommandPalette } from "./CommandPalette";
import { buildPaletteActions } from "./paletteActions";
import { SettingsPanel } from "./SettingsPanel";
import { TenantSwitcher } from "./TenantSwitcher";

function deriveThreadTitle(firstUserText: string | undefined): string {
  if (!firstUserText) return "New thread";
  const trimmed = firstUserText.trim().replace(/\s+/g, " ");
  if (trimmed.length <= 60) return trimmed;
  return trimmed.slice(0, 57) + "…";
}

export function AppShell() {
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const requestComposerFocus = useChatStore((s) => s.requestComposerFocus);
  const clear = useChatStore((s) => s.clear);
  const setActiveTenant = useChatStore((s) => s.setActiveTenant);
  const { goToConversation } = useUrlConversationSync();
  useMessageAnchorScroll();
  useJobsPollingDriver();
  useStreamWatchdog();
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { settings, setTheme, setMotion, setTextScale } = useSettings();
  const { jobs } = useJobsPolling();
  const tenants = useMyTenants();
  const openJob = useOpenJobFromHistory();

  function toggleMobileSidebar() {
    const next = !sidebarOpenMobile;
    setSidebarOpenMobile(next);
    if (next) setSidebarCollapsed(false);
  }

  useShortcut(SHORTCUT.OPEN_PALETTE, () => setPaletteOpen((v) => !v));
  useShortcut(SHORTCUT.NEW_THREAD, () => {
    goToConversation(null);
    clear();
    requestComposerFocus();
  });

  const lastMessageLength = messages[messages.length - 1]?.content.length ?? 0;
  const {
    containerRef: scrollRef,
    isAtBottom,
    scrollToBottom,
  } = useAutoScroll([messages.length, lastMessageLength, isStreaming]);

  // Palette "Recent threads" reflects the polled (server-truth) job list, NOT
  // JobsSection's in-flight optimistic edits (undo-window deletes / pending
  // renames) — those live in JobsSection-local state and reconcile within a
  // poll cycle; surfacing server-truth here is the intended, simpler contract.
  const recentJobs = useMemo(() => jobs.filter((j) => !isActive(j.status)).slice(0, 6), [jobs]);

  const paletteActions = useMemo<CommandAction[]>(
    () =>
      buildPaletteActions({
        onFocusComposer: requestComposerFocus,
        onNewThread: () => {
          goToConversation(null);
          clear();
          requestComposerFocus();
        },
        onOpenSettings: () => setSettingsOpen(true),
        onScrollToBottom: () => scrollToBottom(),
        onSetTheme: setTheme,
        onSetMotion: setMotion,
        onToggleSidebar: () => setSidebarCollapsed((v) => !v),
        onSignOut: () => void pca.logoutRedirect(),
        onSetTextScale: setTextScale,
        textScale: settings.textScale,
        tenants,
        onSwitchTenant: setActiveTenant,
        recentJobs,
        onOpenJob: openJob,
      }),
    [
      requestComposerFocus,
      clear,
      scrollToBottom,
      setTheme,
      setMotion,
      goToConversation,
      setTextScale,
      settings.textScale,
      tenants,
      setActiveTenant,
      recentJobs,
      openJob,
    ],
  );

  const firstUserMessage = messages.find((m) => m.role === "user");
  const threadTitle = deriveThreadTitle(firstUserMessage?.content);
  const messageCount = messages.length;

  useEffect(() => {
    const base = "Enterprise RAG";
    document.title = messageCount > 0 && threadTitle ? `${threadTitle} · ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [threadTitle, messageCount]);

  return (
    <div className="flex h-full">
      <a href="#main-content" className="bk-skip-link">
        Skip to main content
      </a>
      {sidebarOpenMobile && (
        <div
          className="bk-anim-backdrop fixed inset-0 z-30 bg-[var(--scrim)] md:hidden"
          onClick={() => setSidebarOpenMobile(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`${sidebarOpenMobile ? "translate-x-0 shadow-[var(--elev-whisper-xl)]" : "-translate-x-full"} fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-[var(--border-default)] bg-[var(--surface-sidebar)] transition-[transform,width] duration-[var(--dur-base)] ease-[var(--ease-out)] md:relative md:translate-x-0 md:shadow-none ${sidebarCollapsed ? "md:w-14" : "md:w-[16.5rem]"}`}
      >
        <div
          className={
            sidebarCollapsed
              ? "flex items-center justify-center px-2 pt-5 pb-3"
              : "px-4 pt-5 pb-4"
          }
        >
          {sidebarCollapsed ? <BrandSymbol /> : <BrandLockup />}
        </div>
        <div className={sidebarCollapsed ? "flex justify-center px-2 pb-2" : "px-3 pb-2"}>
          <button
            type="button"
            onClick={() => {
              goToConversation(null);
              clear();
              requestComposerFocus();
            }}
            title={`New thread (${shortcut("N")})`}
            aria-label="New thread"
            className={
              sidebarCollapsed
                ? "flex h-10 w-10 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-[var(--elev-ring)] transition-[box-shadow,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:shadow-[var(--elev-whisper)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
                : "type-body-sm flex w-full items-center gap-2 rounded-[var(--radius-pill)] bg-[var(--surface-raised)] px-4 py-2 font-medium text-[var(--text-primary)] shadow-[var(--elev-ring)] transition-[box-shadow,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:shadow-[var(--elev-whisper)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
            }
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              <path d="M7 2v10M2 7h10" />
            </svg>
            {!sidebarCollapsed && <span>New thread</span>}
          </button>
        </div>
        {sidebarCollapsed ? (
          <div className="flex-1" aria-hidden="true" />
        ) : (
          <nav aria-label="Jobs" className="flex-1 overflow-y-auto">
            <JobsSection />
          </nav>
        )}
        <div
          className={
            sidebarCollapsed
              ? "flex flex-col items-center gap-1 border-t border-[var(--border-default)] py-3"
              : "flex items-center gap-2 border-t border-[var(--border-default)] px-3 py-3"
          }
        >
          {!sidebarCollapsed && <TenantSwitcher />}
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Settings"
            title="Settings"
            className={
              sidebarCollapsed
                ? "rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
                : "ml-auto rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
            }
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="7" cy="7" r="1.5" />
              <path d="M7 1v1.5M7 11.5V13M13 7h-1.5M2.5 7H1M11.24 2.76l-1.06 1.06M3.82 10.18l-1.06 1.06M11.24 11.24l-1.06-1.06M3.82 3.82l-1.06-1.06" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)] md:block"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {sidebarCollapsed ? <path d="M5 3l4 4-4 4" /> : <path d="M9 3l-4 4 4 4" />}
            </svg>
          </button>
        </div>
      </aside>
      <main id="main-content" tabIndex={-1} className="flex flex-1 min-w-0 flex-col outline-none">
        <header className="flex items-center justify-end gap-2 px-4 py-2 md:hidden">
          <button
            type="button"
            onClick={toggleMobileSidebar}
            aria-label="Toggle sidebar"
            className="-ml-1 rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
          >
            <span aria-hidden="true" className="block h-0.5 w-4 bg-current" />
            <span aria-hidden="true" className="mt-1 block h-0.5 w-4 bg-current" />
            <span aria-hidden="true" className="mt-1 block h-0.5 w-4 bg-current" />
          </button>
          <div className="flex flex-1 justify-center">
            <BrandLockup size="sm" />
          </div>
          {/* Same 24px footprint as the hamburger (28px minus its -ml-1), so the lockup is centred. */}
          <span className="w-6" aria-hidden="true" />
        </header>
        <div className="relative flex-1">
          <div ref={scrollRef} className="absolute inset-0 overflow-y-auto">
            <Thread />
          </div>
          {!isAtBottom && messageCount > 0 && (
            <button
              type="button"
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              title="Scroll to bottom"
              className="absolute bottom-4 right-6 flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--surface-raised)] text-[var(--text-secondary)] shadow-[var(--elev-whisper)] transition-[box-shadow,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[var(--accent-primary)] hover:shadow-[var(--elev-whisper-lg)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                <path d="M7 11l-5-5 1.06-1.06L7 8.88l3.94-3.94L12 6z" />
              </svg>
            </button>
          )}
        </div>
        {messageCount > 0 && (
          <div className="mx-auto w-full max-w-2xl px-4 pb-4 pt-1">
            <Composer />
          </div>
        )}
      </main>
      <ArtifactPanel />
      <CitationDetailPanel />
      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onClose={() => setSettingsOpen(false)}
        onThemeChange={setTheme}
        onMotionChange={setMotion}
        onTextScaleChange={setTextScale}
      />
      <CommandPalette open={paletteOpen} actions={paletteActions} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
